import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Package, ArrowRight } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { readCheckoutOrder, forgetPendingCheckout, reconcilePaidCheckout } from '../utils/checkoutRecovery';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const refreshCartRef = useRef(refreshCart);
  refreshCartRef.current = refreshCart;
  const { token: customerToken } = useCustomerAuth();
  const { token: legacyToken } = useAuth();
  const token = customerToken || legacyToken;
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const [recheck, setRecheck] = useState(0);

  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    let stopped = false;
    let timer;
    const saved = readCheckoutOrder(orderId);
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (saved?.order?.checkout_token) headers['X-Checkout-Token'] = saved.order.checkout_token;
    const pollStatus = async (attempt = 0) => {
      if (stopped) return;
      if (!sessionId) { setStatus('error'); return; }
      setAttempts(attempt);
      try {
        const res = await axios.get(`${API_URL}/api/payments/status/${sessionId}`, { headers });
        if (stopped) return;
        setPaymentData(res.data);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          if (saved?.sessionId && saved.order.id === res.data.order_id) {
            try {
              await reconcilePaidCheckout(saved);
              await refreshCartRef.current?.();
              forgetPendingCheckout(saved.sessionId, saved.order.id);
            } catch { /* Preserve recovery state; cleanup failure is not payment failure. */ }
          }
          // A checkout token is not an account login. Only authenticated portal
          // customers can fetch configuration or be routed into private orders.
          if (customerToken && res.data.order_id) {
            try {
              const cfg = await axios.get(`${API_URL}/api/order-config/order/${res.data.order_id}`, {
                headers: { Authorization: `Bearer ${customerToken}` },
              });
              if (!stopped && cfg.data.items?.some(i => !i.is_byo && (!i.config || i.config.status !== 'complete'))) {
                timer = setTimeout(() => navigate(`/portal/orders/${res.data.order_id}/configure`, { replace: true }), 1800);
              }
            } catch { /* Payment confirmation does not depend on configuration. */ }
          }
          return;
        }
        if (res.data.status === 'expired') { setStatus('expired'); return; }
        if (attempt >= 5) { setStatus('timeout'); return; }
      } catch {
        if (stopped) return;
        if (attempt >= 5) { setStatus('error'); return; }
      }
      timer = setTimeout(() => pollStatus(attempt + 1), 2000);
    };
    setStatus('checking');
    pollStatus();
    return () => { stopped = true; clearTimeout(timer); };
  }, [sessionId, orderId, token, customerToken, navigate, recheck]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 max-w-md w-full p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-[#4A90A4] mx-auto mb-4 animate-spin" />
            <h2 className="text-lg font-bold text-[#1B3A5C] mb-2">Verifying Payment</h2>
            <p className="text-sm text-gray-500">Please wait while we confirm your payment...</p>
            <p className="text-xs text-gray-300 mt-2">Attempt {attempts + 1} of 6</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-[#1B3A5C] mb-2" data-testid="payment-success-title">Payment Successful!</h2>
            <p className="text-sm text-gray-500 mb-6">
              Your payment has been confirmed. Thank you for your order.
              {paymentData?.amount && <span className="block font-semibold text-gray-700 mt-1">Amount: ${paymentData.amount.toFixed(2)}</span>}
            </p>
            {customerToken && paymentData?.order_id && (
              <Link
                to={`/portal/orders/${paymentData.order_id}/configure`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#F5A623] text-[#1B3A5C] font-bold rounded-xl hover:bg-[#E8961B] transition-colors text-sm mb-3"
                data-testid="configure-items-btn"
              >
                Configure My Items <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <div>
              <Link
                to={customerToken ? "/portal/dashboard" : "/portal/login?mode=setup"}
                className="inline-flex items-center gap-2 text-sm text-[#4A90A4] hover:text-[#1B3A5C] transition-colors"
                data-testid="back-to-orders-btn"
              >
                <Package className="w-4 h-4" /> {customerToken ? "View My Orders" : "Set Up Secure Order Access"}
              </Link>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-lg font-bold text-[#1B3A5C] mb-2">Session Expired</h2>
            <p className="text-sm text-gray-500 mb-6">Your payment session expired. Please try again.</p>
            <button onClick={() => setRecheck(v => v + 1)} className="block mx-auto mb-4 text-[#4A90A4] font-semibold">Check Payment Again</button>
            <Link to="/checkout"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B3A5C] text-white font-bold rounded-xl hover:bg-[#152e4a] text-sm">
              Return to Checkout
            </Link>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-[#1B3A5C] mb-2">
              {status === 'timeout' ? 'Status Check Timed Out' : 'Something Went Wrong'}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {status === 'timeout'
                ? "We couldn't confirm your payment status. Please check your email for a confirmation."
                : 'Unable to verify payment. Please check your orders or contact support.'}
            </p>
            <button onClick={() => setRecheck(v => v + 1)} className="block mx-auto mb-4 text-[#4A90A4] font-semibold">Check Payment Again</button>
            <Link to="/checkout"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B3A5C] text-white font-bold rounded-xl hover:bg-[#152e4a] text-sm">
              Return to Checkout
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
